import React, { useMemo, useEffect } from 'react';
import { MarketAnalysisWidget } from './MarketAnalysisWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  selectedSectorFilterAtom,
} from '~/atoms/dashboardAtom';
import {
  GET_AI_ANALYSIS,
  GET_AI_ANALYSIS_ALL_SECTORS,
} from '~/graphql/queries/portfolio';

interface MarketMetric {
  trend: 'up' | 'down' | 'neutral';
  value: string;
  confidence: number;
}

interface AIAnalysisData {
  market_metrics: {
    avg_pe_ratio: MarketMetric;
    news_sentiment: MarketMetric;
    sector_momentum: MarketMetric;
    sector_momentum_breakdown: Record<string, MarketMetric>;
    volatility_index: MarketMetric;
  };
}

interface AIAnalysisResponse {
  EQPortfoliosAIAnalysis: {
    analysis_data: AIAnalysisData;
    createdAt: string;
  }[];
}

export interface MarketAnalysisWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const MarketAnalysisWidgetWrapper: React.FC<
  MarketAnalysisWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const selectedSectorFilter = useAtomValue(selectedSectorFilterAtom);
  const snapshotId = latestSnapshotId;

  // Choose query based on whether sectors are selected
  const query =
    selectedSectorFilter.length > 0
      ? GET_AI_ANALYSIS
      : GET_AI_ANALYSIS_ALL_SECTORS;
  const variables =
    selectedSectorFilter.length > 0
      ? { snapshotId, sectors: selectedSectorFilter }
      : { snapshotId };

  const { data, loading, error, refetch } = useQuery<AIAnalysisResponse>(
    query,
    {
      variables,
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId,
    },
  );

  // Automatically refetch when snapshotId changes
  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  const marketMetrics = useMemo(() => {
    if (
      loading ||
      !data?.EQPortfoliosAIAnalysis?.[0]?.analysis_data?.market_metrics
    ) {
      return [];
    }

    const metrics = data.EQPortfoliosAIAnalysis[0].analysis_data.market_metrics;

    return [
      {
        label: 'Sector Momentum',
        value: metrics.sector_momentum?.value,
        trend: metrics.sector_momentum?.trend,
      },
      {
        label: 'Avg P/E Ratio',
        value: metrics.avg_pe_ratio?.value,
        trend: metrics.avg_pe_ratio?.trend,
      },
      {
        label: 'News Sentiment',
        value: metrics.news_sentiment?.value,
        trend: metrics.news_sentiment?.trend,
      },
      {
        label: 'Volatility Index',
        value: metrics.volatility_index?.value,
        trend: metrics.volatility_index?.trend,
      },
    ];
  }, [data?.EQPortfoliosAIAnalysis, loading]);

  const sectorMomentumBreakdown = useMemo(() => {
    if (
      loading ||
      !data?.EQPortfoliosAIAnalysis?.[0]?.analysis_data?.market_metrics
        ?.sector_momentum_breakdown
    ) {
      return [];
    }
    const breakdown =
      data.EQPortfoliosAIAnalysis[0].analysis_data.market_metrics
        .sector_momentum_breakdown;
    // Convert object to array format
    return Object.entries(breakdown).map(([sector_name, metric]) => ({
      sector_name,
      metric,
    }));
  }, [data?.EQPortfoliosAIAnalysis, loading]);

  // Format and validate timestamp
  const formattedTimestamp = useMemo(() => {
    const timestamp = data?.EQPortfoliosAIAnalysis?.[0]?.createdAt;

    if (!timestamp) return 'N/A';

    try {
      // This matches ISO 8601 format with timezone indicators
      const hasTimezoneInfo = /[+-]\d{2}:\d{2}$|Z$/i.test(timestamp);

      // If no timezone info, assume it's UTC and add Z
      const utcTimestamp = hasTimezoneInfo ? timestamp : `${timestamp}Z`;

      const date = new Date(utcTimestamp);

      // Check if it's a valid date
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format according to user's local timezone and time format preference
      // Use the browser's default time format (respects user's system settings)
      const formatted = date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        // hour12: undefined - let the browser decide based on user's locale/system settings
      });

      return formatted;
    } catch (error) {
      return 'Invalid Date';
    }
  }, [data]);

  return (
    <MarketAnalysisWidget
      marketMetrics={marketMetrics}
      sectorMomentumBreakdown={sectorMomentumBreakdown}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={shortDescription}
      timestamp={formattedTimestamp}
      snapshotId={snapshotId}
    />
  );
};
